import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    IconButton,
    InputBase,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    ChevronLeft,
    ChevronRight,
    Close,
    DeleteOutline,
    EditOutlined,
    Inventory2Outlined,
    LinkOutlined,
    MoreVert,
    PersonOutline,
    ReceiptLongOutlined,
} from "@mui/icons-material";
import MarkdownContent from "../../../components/common/MarkdownContent";
import { ChecklistItem, Task, TaskChanges, TaskComment, TaskDetail, taskService } from "../../../services/taskService";
import { isSubmitShortcut, SUBMIT_SHORTCUT_LABEL } from "../../../utils/keyboard";
import { useTaskStore } from "../TaskContext";
import { NO_RIGHTS, PERMISSION_TEXT } from "../taskPermissions";
import { apiErrorMessage, personName, repeatLabel, timeAgo } from "../taskUtils";
import { Checklist } from "./Checklist";
import { CommentComposer, TaskActivity } from "./TaskActivity";
import { DueMenu, LabelsMenu, PeopleMenu, PriorityMenu, RepeatMenu, StatusMenu } from "./Pickers";
import { DueChip, LabelChip, LinkChips, LinkKind, PriorityFlag, StaffAvatar, StatusPill } from "./TaskBits";
import { LinkPickerDialog, useRecordViewer } from "./LinkedRecords";

interface TaskDrawerProps {
    taskId: string | null;
    onClose: () => void;
    /** Step through the tasks of the current view (← →). */
    onStep?: (direction: -1 | 1) => void;
    hasPrev?: boolean;
    hasNext?: boolean;
}

type MenuKey = "status" | "assignee" | "due" | "priority" | "repeat" | "labels" | "link" | "more";

const isTyping = (target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
};

export const TaskDrawer: React.FC<TaskDrawerProps> = ({ taskId, onClose, onStep, hasPrev, hasNext }) => {
    const store = useTaskStore();
    const { tasks, today, peopleById, meId, rightsFor, patch, upsert, dropLocal, toast } = store;
    const listRow = taskId ? tasks.find((t) => t.task_id === taskId) : undefined;
    const [detail, setDetail] = useState<TaskDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [menu, setMenu] = useState<{ key: MenuKey; anchor: HTMLElement } | null>(null);
    const [linkKind, setLinkKind] = useState<LinkKind | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const viewer = useRecordViewer((message) => toast({ message, severity: "error" }));
    const request = useRef(0);

    // The list row shows at once; the full task (checklist, comments) follows.
    const task: Task | TaskDetail | undefined = detail && detail.task_id === taskId ? detail : listRow;

    useEffect(() => {
        if (!taskId) {
            setDetail(null);
            return;
        }
        const id = ++request.current;
        setLoading(true);
        setNotFound(false);
        taskService
            .get(taskId)
            .then((d) => {
                if (id !== request.current) return;
                setDetail(d);
                upsert(d);
            })
            .catch(() => {
                if (id === request.current) setNotFound(true);
            })
            .finally(() => {
                if (id === request.current) setLoading(false);
            });
    }, [taskId, upsert]);

    // Keep the drawer in step with board/list edits made while it's open.
    useEffect(() => {
        if (!listRow || !detail || listRow.task_id !== detail.task_id) return;
        if (listRow.update_time !== detail.update_time || listRow.status !== detail.status) {
            setDetail((d) => (d ? { ...d, ...listRow } : d));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listRow]);

    const rights = task ? rightsFor(task) : NO_RIGHTS;
    const openMenu = (key: MenuKey) => (e: React.MouseEvent<HTMLElement>) => {
        if (key !== "more" && !rights.canEdit) return;
        setMenu({ key, anchor: e.currentTarget });
    };
    const closeMenu = () => setMenu(null);

    const save = useCallback(
        async (changes: TaskChanges) => {
            if (!task) return;
            // Show the change here at once too.
            setDetail((d) => (d ? { ...d, ...(changes as Partial<TaskDetail>) } : d));
            const saved = await patch(task, changes);
            if (saved) setDetail(saved);
            else if (taskId) taskService.get(taskId).then(setDetail).catch(() => undefined);
        },
        [task, patch, taskId],
    );

    // The activity history picks up checklist/comment changes shortly after.
    const eventsTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => () => clearTimeout(eventsTimer.current), []);
    const refreshEventsSoon = () => {
        const id = taskId;
        if (!id) return;
        clearTimeout(eventsTimer.current);
        eventsTimer.current = setTimeout(() => {
            taskService
                .get(id)
                .then((fresh) => setDetail((d) => (d && d.task_id === fresh.task_id ? { ...d, events: fresh.events } : d)))
                .catch(() => undefined);
        }, 1200);
    };

    // Lists/counts on the board follow checklist & comment changes.
    // (Read through a ref: checklist adds run in an async loop with an older render's callbacks.)
    const detailRef = useRef(detail);
    detailRef.current = detail;
    const syncCounts = (next: Partial<TaskDetail>) => {
        refreshEventsSoon();
        const d = detailRef.current;
        if (!d) return;
        const merged = { ...d, ...next };
        merged.checklist_total = merged.checklist.length;
        merged.checklist_done = merged.checklist.filter((i) => i.is_done).length;
        merged.comment_count = merged.comments.length;
        detailRef.current = merged;
        setDetail(merged);
        upsert(merged);
    };

    const doDelete = async () => {
        if (!task) return;
        setConfirmDelete(false);
        try {
            await taskService.remove(task.task_id);
            dropLocal(task.task_id);
            onClose();
            toast({ message: "Task deleted.", severity: "success" });
        } catch (err) {
            toast({ message: apiErrorMessage(err, "Couldn't delete the task."), severity: "error" });
        }
    };

    const copyLink = async () => {
        if (!task) return;
        const url = `${window.location.origin}/tasks?task=${task.task_id}`;
        try {
            await navigator.clipboard.writeText(url);
            toast({ message: "Link copied.", severity: "success" });
        } catch {
            toast({ message: url, severity: "info" });
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (isTyping(e.target) || menu || e.metaKey || e.ctrlKey || e.altKey) return;
        if (e.key === "ArrowLeft" && hasPrev && onStep) {
            e.preventDefault();
            onStep(-1);
        } else if (e.key === "ArrowRight" && hasNext && onStep) {
            e.preventDefault();
            onStep(1);
        }
    };

    const creator = task?.created_by_user_id ? peopleById.get(task.created_by_user_id) : undefined;
    const assignee = task?.assignee_user_id ? peopleById.get(task.assignee_user_id) : undefined;
    const full = detail && detail.task_id === taskId ? detail : null;

    return (
        <Drawer
            anchor="right"
            open={!!taskId}
            onClose={onClose}
            onKeyDown={onKeyDown}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: 580 },
                    maxWidth: "100%",
                    // Below the app bar (56px tall on phones, 64px from sm up).
                    mt: { xs: "56px", sm: "64px" },
                    height: { xs: "calc(100% - 56px)", sm: "calc(100% - 64px)" },
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
                {task && <StatusPill status={task.status} onClick={rights.canEdit ? openMenu("status") : undefined} />}
                {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                <Box sx={{ flex: 1 }} />
                {onStep && (
                    <>
                        <Tooltip title="Previous task (←)">
                            <span>
                                <IconButton size="small" disabled={!hasPrev} onClick={() => onStep(-1)}>
                                    <ChevronLeft />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Next task (→)">
                            <span>
                                <IconButton size="small" disabled={!hasNext} onClick={() => onStep(1)}>
                                    <ChevronRight />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}
                {task && (
                    <IconButton size="small" onClick={openMenu("more")} aria-label="More">
                        <MoreVert />
                    </IconButton>
                )}
                <Tooltip title="Close (Esc)">
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <Close />
                    </IconButton>
                </Tooltip>
            </Stack>

            {notFound && !task && (
                <Box sx={{ p: 3 }}>
                    <Alert severity="info">This task doesn't exist any more — it may have been deleted.</Alert>
                </Box>
            )}

            {task && (
                <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 2 }}>
                    <TitleEditor key={task.task_id} value={task.title} canEdit={rights.canEdit} onSave={(title) => save({ title })} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Created by {creator ? personName(creator) : "a former staff member"} · {timeAgo(task.create_time)}
                        {task.status === "DONE" && task.completed_at && (
                            <>
                                {" "}
                                · Done{" "}
                                {task.completed_by_user_id ? `by ${personName(peopleById.get(task.completed_by_user_id))} ` : ""}
                                {timeAgo(task.completed_at)}
                            </>
                        )}
                    </Typography>
                    {!rights.canEdit && (
                        <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0 }}>
                            Only {creator ? personName(creator) : "its creator"}
                            {assignee && assignee.user_id !== creator?.user_id ? `, ${personName(assignee)}` : ""} or an Owner can change
                            this task. You can still comment.
                        </Alert>
                    )}

                    {/* Properties */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "96px 1fr",
                            alignItems: "center",
                            rowGap: 0.75,
                            columnGap: 1.5,
                            mt: 2,
                            mb: 2.5,
                        }}
                    >
                        <PropLabel>Assignee</PropLabel>
                        <PropButton onClick={openMenu("assignee")} disabled={!rights.canEdit}>
                            <StaffAvatar person={assignee} userId={task.assignee_user_id} size={24} tooltip={false} />
                            <Typography variant="body2" sx={{ fontWeight: 500, color: assignee ? "text.primary" : "text.secondary" }}>
                                {assignee ? personName(assignee) : task.assignee_user_id ? "Former staff member" : "Unassigned"}
                                {assignee?.is_active === false && (
                                    <Typography component="span" variant="body2" color="warning.main">
                                        {" "}
                                        (deactivated)
                                    </Typography>
                                )}
                                {assignee?.user_id === meId && (
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {" "}
                                        (you)
                                    </Typography>
                                )}
                            </Typography>
                        </PropButton>

                        <PropLabel>Due</PropLabel>
                        <PropButton onClick={openMenu("due")} disabled={!rights.canEdit}>
                            <DueChip task={task} today={today} showEmpty />
                        </PropButton>

                        <PropLabel>Priority</PropLabel>
                        <PropButton onClick={openMenu("priority")} disabled={!rights.canEdit}>
                            <PriorityFlag priority={task.priority} withLabel />
                        </PropButton>

                        <PropLabel>Repeat</PropLabel>
                        <PropButton onClick={openMenu("repeat")} disabled={!rights.canEdit}>
                            <Typography variant="body2" color={task.repeat_rule ? "text.primary" : "text.secondary"}>
                                {repeatLabel(task.repeat_rule, task.repeat_interval, task.due_date)}
                            </Typography>
                        </PropButton>

                        <PropLabel>Labels</PropLabel>
                        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5} alignItems="center" sx={{ minHeight: 32 }}>
                            {task.labels.map((l) => (
                                <LabelChip key={l} label={l} onDelete={rights.canEdit ? () => save({ labels: task.labels.filter((x) => x !== l) }) : undefined} />
                            ))}
                            {rights.canEdit && (
                                <Button size="small" startIcon={<Add />} onClick={openMenu("labels")} sx={{ textTransform: "none", color: "text.secondary" }}>
                                    {task.labels.length ? "Edit" : "Add label"}
                                </Button>
                            )}
                            {!rights.canEdit && task.labels.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    None
                                </Typography>
                            )}
                        </Stack>

                        <PropLabel>Linked</PropLabel>
                        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5} alignItems="center" sx={{ minHeight: 32 }}>
                            <LinkChips
                                task={task}
                                onOpen={viewer.open}
                                onDelete={rights.canEdit ? (kind) => save({ [`${kind}_id`]: null } as TaskChanges) : undefined}
                            />
                            {rights.canEdit && (!task.order_id || !task.customer_id || !task.product_id) && (
                                <Button size="small" startIcon={<LinkOutlined />} onClick={openMenu("link")} sx={{ textTransform: "none", color: "text.secondary" }}>
                                    Link
                                </Button>
                            )}
                            {!rights.canEdit && !task.order_id && !task.customer_id && !task.product_id && (
                                <Typography variant="body2" color="text.secondary">
                                    Nothing
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    <DescriptionEditor key={`d-${task.task_id}`} value={task.description} canEdit={rights.canEdit} onSave={(description) => save({ description })} />

                    <Divider sx={{ my: 2.5 }} />
                    {full ? (
                        <>
                            <Checklist
                                taskId={full.task_id}
                                items={full.checklist}
                                canEdit={rights.canEdit}
                                onChange={(checklist: ChecklistItem[]) => syncCounts({ checklist })}
                            />
                            {(rights.canEdit || full.checklist.length > 0) && <Divider sx={{ my: 2.5 }} />}
                            <TaskActivity
                                taskId={full.task_id}
                                comments={full.comments}
                                events={full.events}
                                onCommentsChange={(comments: TaskComment[]) => syncCounts({ comments })}
                            />
                        </>
                    ) : (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress size={24} />
                        </Stack>
                    )}
                </Box>
            )}

            {full && (
                <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: "divider", bgcolor: "#f8faf9" }}>
                    <CommentComposer
                        taskId={full.task_id}
                        onPosted={(c) => {
                            syncCounts({ comments: [...full.comments, c] });
                            // The comment event joins the history on the next load.
                        }}
                    />
                </Box>
            )}

            {/* Menus */}
            {task && (
                <>
                    <StatusMenu
                        anchorEl={menu?.key === "status" ? menu.anchor : null}
                        onClose={closeMenu}
                        value={task.status}
                        onSelect={(status) => save({ status })}
                    />
                    <PeopleMenu
                        anchorEl={menu?.key === "assignee" ? menu.anchor : null}
                        onClose={closeMenu}
                        value={task.assignee_user_id}
                        onSelect={(assignee_user_id) => assignee_user_id !== task.assignee_user_id && save({ assignee_user_id })}
                    />
                    <DueMenu
                        anchorEl={menu?.key === "due" ? menu.anchor : null}
                        onClose={closeMenu}
                        date={task.due_date}
                        time={task.due_time}
                        today={today}
                        onSelect={(due_date, due_time) => save({ due_date, due_time })}
                    />
                    <PriorityMenu
                        anchorEl={menu?.key === "priority" ? menu.anchor : null}
                        onClose={closeMenu}
                        value={task.priority}
                        onSelect={(priority) => save({ priority })}
                    />
                    <RepeatMenu
                        anchorEl={menu?.key === "repeat" ? menu.anchor : null}
                        onClose={closeMenu}
                        rule={task.repeat_rule}
                        interval={task.repeat_interval}
                        dueDate={task.due_date}
                        today={today}
                        onSelect={(repeat_rule, repeat_interval) => save({ repeat_rule, repeat_interval })}
                    />
                    <LabelsMenu
                        anchorEl={menu?.key === "labels" ? menu.anchor : null}
                        onClose={closeMenu}
                        value={task.labels}
                        onChange={(labels) => save({ labels })}
                    />
                    <Menu anchorEl={menu?.key === "link" ? menu.anchor : null} open={menu?.key === "link"} onClose={closeMenu}>
                        {!task.order_id && (
                            <MenuItem onClick={() => { closeMenu(); setLinkKind("order"); }}>
                                <ListItemIcon><ReceiptLongOutlined fontSize="small" /></ListItemIcon>
                                Order
                            </MenuItem>
                        )}
                        {!task.customer_id && (
                            <MenuItem onClick={() => { closeMenu(); setLinkKind("customer"); }}>
                                <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
                                Customer
                            </MenuItem>
                        )}
                        {!task.product_id && (
                            <MenuItem onClick={() => { closeMenu(); setLinkKind("product"); }}>
                                <ListItemIcon><Inventory2Outlined fontSize="small" /></ListItemIcon>
                                Product
                            </MenuItem>
                        )}
                    </Menu>
                    <Menu anchorEl={menu?.key === "more" ? menu.anchor : null} open={menu?.key === "more"} onClose={closeMenu}>
                        <MenuItem onClick={() => { closeMenu(); copyLink(); }}>
                            <ListItemIcon><LinkOutlined fontSize="small" /></ListItemIcon>
                            Copy link
                        </MenuItem>
                        {rights.canDelete ? (
                            <MenuItem onClick={() => { closeMenu(); setConfirmDelete(true); }} sx={{ color: "error.main" }}>
                                <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
                                Delete task
                            </MenuItem>
                        ) : (
                            <MenuItem disabled sx={{ whiteSpace: "normal", maxWidth: 260 }}>
                                <ListItemIcon><DeleteOutline fontSize="small" color="disabled" /></ListItemIcon>
                                <ListItemText primary="Delete task" secondary={PERMISSION_TEXT.delete} />
                            </MenuItem>
                        )}
                    </Menu>
                </>
            )}

            <LinkPickerDialog
                kind={linkKind}
                onClose={() => setLinkKind(null)}
                onPick={(link) => save({ [`${link.kind}_id`]: link.id } as TaskChanges)}
            />

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete this task?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        “{task?.title}” will disappear for everyone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={doDelete} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {viewer.element}
        </Drawer>
    );
};

// ── Pieces ──────────────────────────────────────────────────────────────────

const PropLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        {children}
    </Typography>
);

const PropButton: React.FC<{ children: React.ReactNode; onClick: (e: React.MouseEvent<HTMLElement>) => void; disabled?: boolean }> = ({
    children,
    onClick,
    disabled,
}) => (
    <ButtonBase
        onClick={onClick}
        disabled={disabled}
        sx={{
            justifySelf: "start",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            minHeight: 32,
            px: 1,
            mx: -1,
            borderRadius: 1.5,
            "&:hover": { bgcolor: "action.hover" },
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
        }}
    >
        {children}
    </ButtonBase>
);

const TitleEditor: React.FC<{ value: string; canEdit: boolean; onSave: (title: string) => void }> = ({ value, canEdit, onSave }) => {
    const [text, setText] = useState(value);
    useEffect(() => setText(value), [value]);
    const commit = () => {
        const clean = text.replace(/\s+/g, " ").trim();
        if (!clean) setText(value);
        else if (clean !== value) onSave(clean.slice(0, 200));
    };
    return (
        <InputBase
            fullWidth
            multiline
            value={text}
            readOnly={!canEdit}
            onChange={(e) => setText(e.target.value.replace(/\n/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLElement).blur();
                } else if (e.key === "Escape") {
                    e.stopPropagation();
                    setText(value);
                    (e.target as HTMLElement).blur();
                }
            }}
            inputProps={{ "aria-label": "Task title", maxLength: 200 }}
            sx={{
                fontSize: 21,
                fontWeight: 650,
                lineHeight: 1.3,
                color: "#10282c",
                borderRadius: 1,
                mx: -0.75,
                px: 0.75,
                "&:hover": canEdit ? { bgcolor: "action.hover" } : undefined,
                "&.Mui-focused": { bgcolor: "action.hover" },
            }}
        />
    );
};

const DescriptionEditor: React.FC<{ value: string | null; canEdit: boolean; onSave: (text: string | null) => void }> = ({
    value,
    canEdit,
    onSave,
}) => {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(value || "");
    useEffect(() => {
        if (!editing) setText(value || "");
    }, [value, editing]);
    const commit = () => {
        setEditing(false);
        const clean = text.trim();
        if (clean !== (value || "").trim()) onSave(clean || null);
    };

    if (editing) {
        return (
            <Box>
                <TextField
                    fullWidth
                    multiline
                    autoFocus
                    minRows={4}
                    value={text}
                    placeholder="Details, steps, phone numbers… Markdown works (**bold**, - lists)."
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (isSubmitShortcut(e.nativeEvent)) {
                            e.preventDefault();
                            commit();
                        } else if (e.key === "Escape") {
                            e.stopPropagation();
                            setText(value || "");
                            setEditing(false);
                        }
                    }}
                    slotProps={{ htmlInput: { maxLength: 10000 } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button size="small" color="inherit" onClick={() => { setText(value || ""); setEditing(false); }}>
                        Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={commit}>
                        Save <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.75 }}>{SUBMIT_SHORTCUT_LABEL}</Typography>
                    </Button>
                </Stack>
            </Box>
        );
    }
    if (!value) {
        return canEdit ? (
            <ButtonBase
                onClick={() => setEditing(true)}
                sx={{ width: "100%", justifyContent: "flex-start", px: 1.5, py: 1.25, borderRadius: 1.5, border: "1px dashed", borderColor: "divider", color: "text.secondary", fontSize: 14 }}
            >
                <EditOutlined sx={{ fontSize: 17, mr: 1 }} /> Add a description
            </ButtonBase>
        ) : null;
    }
    return (
        <Box
            onClick={canEdit ? () => setEditing(true) : undefined}
            sx={{
                position: "relative",
                borderRadius: 1.5,
                mx: -1,
                px: 1,
                py: 0.5,
                cursor: canEdit ? "text" : "default",
                "&:hover": canEdit ? { bgcolor: "action.hover" } : undefined,
                "& p:first-of-type": { mt: 0 },
            }}
        >
            <MarkdownContent content={value} />
        </Box>
    );
};
